import { useState } from 'react'

interface MarkdownRendererProps {
  content: string
  onEdit?: () => void
  showEditButton?: boolean
  className?: string
}

export function MarkdownRenderer({ 
  content, 
  onEdit, 
  showEditButton = true,
  className = "" 
}: MarkdownRendererProps) {
  const [isHovered, setIsHovered] = useState(false)

  // 高度なMarkdown to HTMLコンバーター
  const renderMarkdown = (markdown: string) => {
    if (!markdown.trim()) {
      return '<div class="text-gray-400 italic">議事録の内容がありません</div>'
    }

    // コードブロック内のmarkdownを通常のマークダウンとして処理
    let processedMarkdown = markdown.replace(/```markdown\n([\s\S]*?)```/g, (match, content) => {
      return content
    })

    // ネクストアクションセクションを表形式に変換
    processedMarkdown = processedMarkdown.replace(
      /##\s*ネクストアクション\s*\n([\s\S]*?)(?=\n##|\n---|\n$)/,
      (match, content) => {
        // 各アクション項目を解析
        const actionLines = content.trim().split('\n')
        const actions: Array<{
          number: string
          person: string
          deadline: string
          task: string
          details: string[]
        }> = []
        
        let currentAction: any = null

        actionLines.forEach((line: string) => {
          // 形式A: 1. **担当者**（期限）: タスク
          const mainMatchA = line.match(/^(\d+)\.\s*\*\*([^*]+)\*\*[（(]([^)）]+)[)）][:：]\s*(.+)$/)
          // 形式B（生成ルール標準）: 1. **担当者**：タスク（期限まで）
          const mainMatchB = line.match(/^(\d+)\.\s*\*\*([^*]+)\*\*[:：]\s*(.+)$/)
          if (mainMatchA) {
            if (currentAction) actions.push(currentAction)
            currentAction = {
              number: mainMatchA[1],
              person: mainMatchA[2].trim(),
              deadline: mainMatchA[3].trim(),
              task: mainMatchA[4].trim(),
              details: []
            }
          } else if (mainMatchB) {
            if (currentAction) actions.push(currentAction)
            // タスク末尾の「（〜まで）」「（期限: 〜）」を期限として抽出
            let task = mainMatchB[3].trim()
            let deadline = ''
            const dl = task.match(/[（(]\s*(?:期限[:：]?\s*)?([^）)]*?(?:まで|中|末|日|週|月|旬|翌|次回|未定|随時|[0-9]))\s*[)）]\s*$/)
            if (dl) {
              deadline = dl[1].replace(/まで$/, '').trim()
              task = task.slice(0, dl.index).trim()
            }
            currentAction = {
              number: mainMatchB[1],
              person: mainMatchB[2].trim(),
              deadline,
              task,
              details: []
            }
          }
          // ネストされた詳細項目
          else if (line.trim().startsWith('-') && currentAction) {
            const detail = line.trim().replace(/^-\s*/, '')
            currentAction.details.push(detail)
          }
        })

        if (currentAction) {
          actions.push(currentAction)
        }

        // アクションが1件もない場合:
        // - 本文に何か書かれているならMarkdownのまま表示（表化で内容を消さない）
        // - 実質空ならセクション自体を非表示にする
        if (actions.length === 0) {
          const meaningful = content.replace(/[\s\-*|]/g, '')
          return meaningful ? match : ''
        }

        // 表形式のHTMLを生成
        let tableHtml = '<h2 id="ネクストアクション" class="text-xl font-semibold text-gray-900 mt-8 mb-4">ネクストアクション</h2>'
        tableHtml += '<div class="overflow-x-auto my-6"><table class="min-w-full divide-y divide-gray-200 border border-gray-200">'
        tableHtml += '<thead class="bg-gray-50"><tr>'
        tableHtml += '<th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 w-12">No</th>'
        tableHtml += '<th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">担当者</th>'
        tableHtml += '<th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">期限</th>'
        tableHtml += '<th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200">タスク</th>'
        tableHtml += '<th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">詳細</th>'
        tableHtml += '</tr></thead>'
        tableHtml += '<tbody class="bg-white divide-y divide-gray-200">'
        
        actions.forEach(action => {
          tableHtml += '<tr class="hover:bg-gray-50">'
          tableHtml += `<td class="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 text-center font-medium">${action.number}</td>`
          tableHtml += `<td class="px-4 py-3 text-sm text-gray-900 border-r border-gray-200 font-semibold">${action.person}</td>`
          tableHtml += `<td class="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">${action.deadline}</td>`
          tableHtml += `<td class="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">${action.task}</td>`
          tableHtml += '<td class="px-4 py-3 text-sm text-gray-600">'
          if (action.details.length > 0) {
            tableHtml += '<ul class="list-disc list-inside space-y-1">'
            action.details.forEach((detail: string) => {
              tableHtml += `<li>${detail}</li>`
            })
            tableHtml += '</ul>'
          } else {
            tableHtml += '-'
          }
          tableHtml += '</td>'
          tableHtml += '</tr>'
        })
        
        tableHtml += '</tbody></table></div>'
        
        return tableHtml
      }
    )

    // テーブルを処理（コードブロックより先に処理）
    processedMarkdown = processedMarkdown.replace(/(\|.+\|[\r\n]+\|[\s\-:|]+\|[\r\n]+(?:\|.+\|[\r\n]*)+)/g, (match) => {
      const lines = match.trim().split('\n')
      if (lines.length < 2) return match
      
      // ヘッダー行
      const headers = lines[0].split('|').filter(cell => cell.trim()).map(cell => cell.trim())
      
      // 区切り行をスキップ
      
      // データ行
      const rows = lines.slice(2).map(line => 
        line.split('|').filter(cell => cell.trim()).map(cell => cell.trim())
      )
      
      let tableHtml = '<div class="overflow-x-auto my-6"><table class="min-w-full divide-y divide-gray-200 border border-gray-200">'
      
      // ヘッダー
      tableHtml += '<thead class="bg-gray-50"><tr>'
      headers.forEach(header => {
        tableHtml += `<th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-200 last:border-r-0">${header}</th>`
      })
      tableHtml += '</tr></thead>'
      
      // ボディ
      tableHtml += '<tbody class="bg-white divide-y divide-gray-200">'
      rows.forEach(row => {
        tableHtml += '<tr class="hover:bg-gray-50">'
        row.forEach(cell => {
          tableHtml += `<td class="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 last:border-r-0">${cell}</td>`
        })
        tableHtml += '</tr>'
      })
      tableHtml += '</tbody></table></div>'
      
      return tableHtml
    })

    processedMarkdown = processedMarkdown
      // その他のコードブロック
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto"><code class="text-sm font-mono">$1</code></pre>')
      // インラインコード
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono">$1</code>')
      // ヘッダー（IDを追加してアンカーリンクを有効化）
      .replace(/^#### (.*$)/gim, (match, text) => {
        const id = text.trim().replace(/\s+/g, '-').replace(/[^\w\-\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/g, '')
        return `<h4 id="${id}" class="text-base font-semibold text-gray-900 mt-6 mb-2">${text}</h4>`
      })
      .replace(/^### (.*$)/gim, (match, text) => {
        const id = text.trim().replace(/\s+/g, '-').replace(/[^\w\-\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/g, '')
        return `<h3 id="${id}" class="text-lg font-semibold text-gray-900 mt-6 mb-3">${text}</h3>`
      })
      .replace(/^## (.*$)/gim, (match, text) => {
        const id = text.trim().replace(/\s+/g, '-').replace(/[^\w\-\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/g, '')
        return `<h2 id="${id}" class="text-xl font-semibold text-gray-900 mt-8 mb-4">${text}</h2>`
      })
      .replace(/^# (.*$)/gim, (match, text) => {
        const id = text.trim().replace(/\s+/g, '-').replace(/[^\w\-\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/g, '')
        return `<h1 id="${id}" class="text-2xl font-bold text-gray-900 mt-8 mb-6">${text}</h1>`
      })
      // 太字
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      // イタリック
      .replace(/\*(.*?)\*/g, '<em class="italic text-gray-700">$1</em>')
      // 取り消し線
      .replace(/~~(.*?)~~/g, '<del class="line-through text-gray-500">$1</del>')
      // チェックボックス
      .replace(/- \[x\] (.*$)/gim, '<div class="flex items-center gap-2 my-1"><input type="checkbox" checked disabled class="rounded text-green-600"><span class="text-gray-700">$1</span></div>')
      .replace(/- \[ \] (.*$)/gim, '<div class="flex items-center gap-2 my-1"><input type="checkbox" disabled class="rounded text-gray-400"><span class="text-gray-700">$1</span></div>')
      // 番号付きリスト（改行を削除してから処理）
      .replace(/^(\d+)\. (.*$)/gim, (match, num, text) => {
        return `<div class="flex gap-2 my-0.5"><span class="text-gray-600 font-medium min-w-[1.5rem]">${num}.</span><span class="text-gray-700">${text.trim()}</span></div>`
      })
      // ネストされたリスト（3つ以上のスペース + ハイフン）
      .replace(/^   [\-\*] (.*$)/gim, (match, text) => {
        return `<div class="flex gap-2 my-0.5 ml-8 pl-4 border-l-2 border-gray-200"><span class="text-gray-500 min-w-[1rem]">◦</span><span class="text-gray-600">${text.trim()}</span></div>`
      })
      // 通常のリスト（改行を削除してから処理）
      .replace(/^[\-\*] (.*$)/gim, (match, text) => {
        return `<div class="flex gap-2 my-0.5"><span class="text-gray-600 min-w-[1rem]">•</span><span class="text-gray-700">${text.trim()}</span></div>`
      })
      // 引用
      .replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-blue-200 pl-4 my-4 text-gray-600 italic">$1</blockquote>')
      // 水平線
      .replace(/^---$/gim, '<hr class="border-gray-300 my-6">')
      // リンク（アンカーリンクと外部リンクを区別）
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        if (url.startsWith('#')) {
          // アンカーリンク
          return `<a href="${url}" class="text-blue-600 hover:text-blue-800 underline">${text}</a>`
        } else {
          // 外部リンク
          return `<a href="${url}" class="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">${text}</a>`
        }
      })
      // リスト項目間の改行を削除
      .replace(/(<\/div>)\n(<div class="flex)/g, '$1$2')
      // 段落（連続する改行を段落に変換）
      .replace(/\n\n/g, '</p><p class="mb-4">')
      // 単一改行をbrに変換（ただしリスト内は除外）
      .replace(/\n/g, '<br>')
      // 段落タグで囲む
      .replace(/^(.+)/, '<p class="mb-4">$1')
      .replace(/(.+)$/, '$1</p>')
    
    return processedMarkdown
  }

  return (
    <div className={className}>
      {/* レンダリングされたコンテンツ */}
      <div 
        className="prose max-w-none leading-relaxed"
        dangerouslySetInnerHTML={{ 
          __html: renderMarkdown(content) 
        }}
        style={{
          lineHeight: '1.7'
        }}
      />

      {/* 空の状態 */}
      {!content.trim() && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">議事録がありません</h3>
          <p className="text-gray-500 mb-4">AI整形を実行して議事録を生成してください</p>
          {showEditButton && onEdit && (
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              編集を開始
            </button>
          )}
        </div>
      )}
    </div>
  )
}
